import * as jose from 'jose';

import { getRestApiUrl } from '../../api/config';
import { OAUTH_PROVIDERS, validateOAuthNonce } from './oauthRequestSecurity';

/**
 * The `user-web-app` google authentication flow follows the OpenID Connect protocol.
 * As part of this protocol, the provider can use an identity document.
 * This document is available at the following URI and contains key-value pairs which provide details about the provider's configuration.
 * More information is available here: https://developers.google.com/identity/openid-connect/openid-connect#discovery
 */
const GOOGLE_IDENTITY_DOCUMENT_URL = 'https://accounts.google.com/.well-known/openid-configuration';

/**
 * For more information about the specific fields, see: https://openid.net/specs/openid-connect-discovery-1_0.html#ProviderMetadata
 */
export interface GoogleIdentityDocument {
    issuer: string;
    authorization_endpoint: string;
    device_authorization_endpoint: string;
    token_endpoint: string;
    userinfo_endpoint: string;
    revocation_endpoint: string;
    jwks_uri: string;
    response_types_supported: string[];
    subject_types_supported: string[];
    id_token_signing_alg_values_supported: string[];
    scopes_supported: string[];
    token_endpoint_auth_methods_supported: string[];
    claims_supported: string[];
    code_challenge_methods_supported: string[];
    grant_types_supported: string[];
}

export async function getSocialIdentityDocument(): Promise<GoogleIdentityDocument> {
    console.log('Google API: fetching identity document...');
    const identityDocumentResponse = await fetch(GOOGLE_IDENTITY_DOCUMENT_URL);
    console.log('Google API: identity document fetched');
    return identityDocumentResponse.json();
}

export const GOOGLE_OAUTH_CLIENT_ID = '424755138228-4827vp1dnem3ve06dra346mtflgqggma.apps.googleusercontent.com';

export async function exchangeGoogleCodeForIdToken(code: string, redirectUrl: string): Promise<string> {
    try {
        console.log('Google API: exchanging user code for tokens...');
        const headers = new Headers();
        headers.append('Accept', 'application/json');
        const url = new URL(`${getRestApiUrl()}user/exchange-google-code`);
        url.searchParams.append('googleClientId', GOOGLE_OAUTH_CLIENT_ID);
        url.searchParams.append('redirectUrl', redirectUrl);
        url.searchParams.append('code', code);
        const tokenResponse = await fetch(url.toString(), { method: 'GET', headers });
        const tokenResponseJson = await tokenResponse.json();
        console.log('Google API: user code exchanged');
        return tokenResponseJson.id_token;
    } catch (error) {
        throw new GoogleLoginError(error);
    }
}

export class GoogleLoginError extends Error {
    __proto__: GoogleLoginError;
    message: string;
    constructor(error: unknown) {
        let message: string;
        if (error instanceof Error) message = error.message;
        else if (typeof error === 'string') message = error;
        else message = 'Unknown error';

        super(`Google login (${message})`);
        this.__proto__ = GoogleLoginError.prototype;
        this.message = message;
    }
}

interface GoogleIdToken {
    sub: string;
    at_hash?: string;
    azp?: string;
    email?: string;
    email_verified?: boolean;
    family_name?: string;
    given_name?: string;
    name?: string;
    picture?: string;
    hd?: string;
    locale?: string;
    nonce?: string;
    profile?: string;
}

export async function verifyAndDecodeGoogleJsonWebToken(
    idJsonWebToken: string,
    identityDocument: GoogleIdentityDocument,
    expectedNonce: string
): Promise<GoogleIdToken> {
    // Create a remote JWK set that fetches keys from Google's JWKS endpoint
    const JWKS = jose.createRemoteJWKSet(new URL(identityDocument.jwks_uri));
    // Verify the JWT using the remote JWKS with issuer and audience validation
    const { payload } = await jose.jwtVerify(idJsonWebToken, JWKS, {
        algorithms: ['RS256'],
        issuer: ['https://accounts.google.com', 'accounts.google.com'],
        audience: GOOGLE_OAUTH_CLIENT_ID,
    });
    if (!payload || !payload.sub || typeof payload.sub !== 'string')
        throw new Error(`Google login: missing 'sub' key in verified JWT Token`);
    validateOAuthNonce(
        OAUTH_PROVIDERS.GOOGLE,
        expectedNonce,
        typeof payload.nonce === 'string' ? payload.nonce : undefined
    );
    return payload as unknown as GoogleIdToken;
}
